/* ==========================================================================
   Sticky nav retune
   --------------------------------------------------------------------------
   Turbo builds the sticky header with Headhesive, configured in sections.js
   with `offset: 700` and `throttle: 300`. Two consequences:

     - Pages with less than ~700px of scroll never show a sticky nav at all,
       which is most product pages, the cart, and every policy page.
     - The scroll handler runs at most every 300ms, so it lags noticeably.

   The theme serves sections.min.js (settings.minify is on), so editing the
   readable source would have no effect. Instead we retune the live instance:
   Headhesive exposes it as window.sticky_nav, reads this.scrollOffset fresh on
   every check, and guards stick()/unstick() behind a `visible` flag so calling
   them repeatedly is harmless.

   What this does:
     1. Moves the trigger point to the bottom of the real header, so the
        sticky nav appears exactly as the header scrolls out of view.
     2. Adds a requestAnimationFrame scroll listener that drives stick() and
        unstick() directly, removing the 300ms lag. The theme's own throttled
        handler keeps running and simply agrees with us.
     3. Recomputes the trigger point on resize, and on load, since the header
        height changes with the promo banner and with breakpoints.

   Nothing in the theme is modified. If Turbo ever changes these defaults,
   this file becomes a no-op rather than a conflict.
   ========================================================================== */
(function () {
  'use strict';

  var FALLBACK_OFFSET = 120; // used if the header can't be measured
  var MAX_WAIT_MS = 8000; // how long to wait for Headhesive to initialise
  var POLL_MS = 100;

  var instance = null;
  var ticking = false;

  /* --------------------------------------------------------------- measure */

  // The real (non-cloned) header wrapper. Headhesive clones .main-nav__wrapper
  // and gives the copy a .sticky_nav class, so exclude that one.
  function realNav() {
    var candidates = document.querySelectorAll('.main-nav__wrapper');

    for (var i = 0; i < candidates.length; i++) {
      if (!candidates[i].classList.contains('sticky_nav')) {
        return candidates[i];
      }
    }

    return null;
  }

  function triggerPoint() {
    var nav = realNav();
    if (!nav) return FALLBACK_OFFSET;

    var rect = nav.getBoundingClientRect();
    var bottom = rect.top + (window.pageYOffset || document.documentElement.scrollTop) + rect.height;

    // Guard against a zero/absurd measurement while the page is still settling.
    return bottom > 0 ? Math.round(bottom) : FALLBACK_OFFSET;
  }

  function applyOffset() {
    if (!instance) return;
    instance.scrollOffset = triggerPoint();
    update();
  }

  /* ---------------------------------------------------------------- update */

  function update() {
    if (!instance) return;

    var y = window.pageYOffset || document.documentElement.scrollTop;

    if (y > instance.scrollOffset) {
      instance.stick();
    } else {
      instance.unstick();
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;

    window.requestAnimationFrame(function () {
      ticking = false;
      update();
    });
  }

  /* ------------------------------------------------------------------ init */

  function start() {
    applyOffset();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', applyOffset);
    window.addEventListener('orientationchange', applyOffset);

    // The promo banner, web fonts and images can all change header height
    // after DOMContentLoaded, so remeasure once everything has settled.
    window.addEventListener('load', applyOffset);
    window.setTimeout(applyOffset, 1000);
  }

  // Headhesive is constructed inside the theme's section init, which runs after
  // its deferred bundles execute. Poll briefly rather than guess at timing.
  function waitForHeadhesive() {
    var waited = 0;

    var timer = window.setInterval(function () {
      if (window.sticky_nav && typeof window.sticky_nav.stick === 'function') {
        window.clearInterval(timer);
        instance = window.sticky_nav;
        start();
        return;
      }

      waited += POLL_MS;
      if (waited >= MAX_WAIT_MS) {
        window.clearInterval(timer);
      }
    }, POLL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForHeadhesive);
  } else {
    waitForHeadhesive();
  }
})();
